/**
 * FieldLegend
 *
 * A key for the field: what the glyphs mean and what the colours mean.
 *
 * ## Why this exists
 *
 * There was no legend anywhere. Nothing told a first-time user that `×` is offence, `○` is defence
 * and `□` is the centre, and nothing named the colour scheme. The player labels (WR1, CB2, FS) did
 * the work, but a coach opening the simulator for the first time had to infer the whole visual
 * language from context.
 *
 * ## Why it is this short
 *
 * It only explains what is genuinely ambiguous. Per-position colours are not listed, because after
 * the palette rework defence is coloured by LEVEL rather than by individual — so there are exactly
 * two defensive colours to explain rather than seven. That is the legend earning its keep: a
 * scheme you can describe in two lines is a better scheme than one that needs a fourteen-row table.
 */

import './FieldLegend.css';

interface LegendSwatch {
  glyph: string;
  label: string;
  colorVar: string;
}

/** Offence: one warm hue per skill player, because you follow an individual route. */
const OFFENSE: LegendSwatch[] = [
  { glyph: '×', label: 'WR1', colorVar: 'var(--color-off-wr1)' },
  { glyph: '×', label: 'WR2', colorVar: 'var(--color-off-wr2)' },
  { glyph: '×', label: 'WR3', colorVar: 'var(--color-off-wr3)' },
  { glyph: '×', label: 'WR4', colorVar: 'var(--color-off-wr4)' },
  { glyph: '×', label: 'RB', colorVar: 'var(--color-off-rb)' },
];

/** Defence: by level, because that is how a coverage is read. */
const DEFENSE: LegendSwatch[] = [
  { glyph: '○', label: 'Deep — corners & safeties', colorVar: 'var(--color-def-deep)' },
  { glyph: '○', label: 'Underneath — apexes & linebacker', colorVar: 'var(--color-def-underneath)' },
];

function SwatchRow({ items }: { items: LegendSwatch[] }) {
  return (
    <ul className="legend-items">
      {items.map((item) => (
        <li className="legend-item" key={item.label}>
          <span className="legend-glyph" style={{ color: item.colorVar }} aria-hidden="true">
            {item.glyph}
          </span>
          <span className="legend-label">{item.label}</span>
        </li>
      ))}
    </ul>
  );
}

function FieldLegend() {
  return (
    <div className="field-legend">
      <div className="legend-group">
        <h3 className="legend-heading">Offense</h3>
        <SwatchRow items={OFFENSE} />
      </div>
      <div className="legend-group">
        <h3 className="legend-heading">Defense</h3>
        <SwatchRow items={DEFENSE} />
      </div>
      <p className="legend-note">
        Yard numbers show depth from the line of scrimmage. Click any offensive player to change his
        route.
      </p>
    </div>
  );
}

export default FieldLegend;
